// Core
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import { FieldSearchSelect, FieldSelect, RemoteTable } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetTransactionAdjustmentsByCommunityIdPaginationQuery } from "state/sessions/sessionApiSlice";

// Utils
import { useGetResources } from "utils/crud/read";
import { datetimeToDate, datetimeToTime } from "utils/time";

// Adjustments are sparse (only where measurements were revised), so we load them
// in a single request and paginate/filter/sort client-side.
const LOAD_LIMIT = 1000;
const PAGE_LIMIT = 10;
const ROUND_DECIMALS = 4;

// numeric cell renderer: tabular, right-aligned, fixed decimals (same style as
// the bilateral allocations table)
const num = (decimals) => (color) => (v) =>
  (
    <span className="tabular-nums" style={{ color, fontWeight: 600 }}>
      {v != null ? Number(v).toFixed(decimals) : "—"}
    </span>
  );

const mono = (v) => <span className="font-mono text-[12.5px] text-ink">{v}</span>;

function ListTransactionAdjustments({ setIsLoading }) {
  const community = useSelector(selectCurrentCommunity);
  const resources = useGetResources("true");
  const { t } = useTranslation(["market", "common"]);

  const { data, isFetching } = useGetTransactionAdjustmentsByCommunityIdPaginationQuery(
    { page: 0, limit: LOAD_LIMIT, communityId: community.id },
    { pollingInterval: 15000, refetchOnFocus: true, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  const resourceIdToName = (resourceId) => resources?.find((r) => r.id === resourceId)?.name;

  const [page, setPage] = useState(0);
  const [sortOrder, setSortOrder] = useState(undefined);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [resource, setResource] = useState("");

  const rows = useMemo(
    () =>
      (data?.data ?? []).map((row, i) => ({
        id: row.id ?? `${row.datetime_timestamp}-${i}`,
        date: datetimeToDate(row.datetime_timestamp),
        time: datetimeToTime(row.datetime_timestamp),
        resource: (row.resource_id && resourceIdToName(row.resource_id)) || "—",
        original_e_met: row.original_e_met,
        adjusted_e_met: row.adjusted_e_met ?? 0,
        e_met_difference: row.e_met_difference ?? 0,
      })),
    [data, resources]
  );

  const filteredRows = useMemo(
    () =>
      rows.filter((r) => {
        if (date && r.date !== date) return false;
        if (time && r.time !== time) return false;
        if (resource && r.resource !== resource) return false;
        return true;
      }),
    [rows, date, time, resource]
  );

  const sortedRows = useMemo(() => {
    if (!sortOrder?.column) return filteredRows;
    const dir = sortOrder.direction === "desc" ? -1 : 1;
    return [...filteredRows].sort(
      (a, b) =>
        String(a[sortOrder.column] ?? "").localeCompare(String(b[sortOrder.column] ?? ""), "pt", {
          numeric: true,
        }) * dir
    );
  }, [filteredRows, sortOrder]);

  const pageRows = useMemo(
    () => sortedRows.slice(page * PAGE_LIMIT, (page + 1) * PAGE_LIMIT),
    [sortedRows, page]
  );

  const value = num(ROUND_DECIMALS);

  const columns = [
    { key: "date", label: t("adjustments.date"), sortable: true, width: "13%" },
    { key: "time", label: t("adjustments.hour"), sortable: true, width: "12%" },
    { key: "resource", label: t("adjustments.resource"), width: "20%", render: mono },
    {
      key: "original_e_met",
      label: t("adjustments.original_energy"),
      align: "right",
      render: value(undefined),
    },
    {
      key: "adjusted_e_met",
      label: t("adjustments.adjusted_energy"),
      align: "right",
      render: value("var(--accent)"),
    },
    {
      key: "e_met_difference",
      label: t("adjustments.difference"),
      align: "right",
      render: value("var(--warm)"),
    },
  ];

  const distinct = (key) => [...new Set(rows.map((r) => r[key]))].filter((v) => v !== "—").sort();
  const toOptions = (allLabel, values) => [
    { value: "", label: allLabel },
    ...values.map((v) => ({ value: v, label: v })),
  ];

  const resetPage = () => setPage(0);
  const onFilter = (setter) => (v) => {
    resetPage();
    setter(v);
  };

  const filters = (
    <>
      <div className="min-w-[170px]">
        <FieldSelect
          value={date}
          options={toOptions(t("adjustments.all_dates"), distinct("date"))}
          onChange={onFilter(setDate)}
        />
      </div>
      <div className="min-w-[150px]">
        <FieldSelect
          value={time}
          options={toOptions(t("adjustments.all_times"), distinct("time"))}
          onChange={onFilter(setTime)}
        />
      </div>
      <div className="min-w-[200px]">
        <FieldSearchSelect
          value={resource}
          onChange={onFilter(setResource)}
          options={toOptions(t("adjustments.all_resources"), distinct("resource"))}
        />
      </div>
    </>
  );

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="rec-section-title !mb-0">
            <Icon name="tune" />
            {t("adjustments.title")}
          </h3>
          <p className="text-[12.5px] text-ink-3 m-0 mt-1">{t("adjustments.subtitle")}</p>
        </div>
      </div>
      <div className="rec-card">
        <RemoteTable
          columns={columns}
          rows={pageRows}
          totalCount={sortedRows.length}
          page={page}
          limit={PAGE_LIMIT}
          onPageChange={setPage}
          sort={sortOrder}
          onSortChange={setSortOrder}
          filters={filters}
          loading={isFetching}
        />
      </div>
    </div>
  );
}

ListTransactionAdjustments.defaultProps = {
  setIsLoading: () => {},
};

ListTransactionAdjustments.propTypes = {
  setIsLoading: PropTypes.func,
};

export default ListTransactionAdjustments;
